/* eslint-disable complexity */
import { type Dictionary, type Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { DateTime } from 'luxon';
import { StudentType } from '../enums/enums.js';
import { ChancenwerkUserFilter } from './ChancenwerkUserFilter.js';
import { buildSimpleFilterMapped } from './Util.js';

/**
 * @entity StudentFilter
 */
@Entity()
export class StudentFilter extends ChancenwerkUserFilter {
  public static override _class = 'StudentFilter';

  constructor(doc: PartialEntityData<StudentFilter>) {
    super(doc);
  }

  @Field(type => String)
  public paymentMethod: Maybe<string>;

  @Field(type => Boolean)
  public butBerechtigung: Maybe<boolean>;

  @Field(type => DateTime)
  public activeOnDay: Maybe<DateTime>;

  @Field(type => Boolean)
  public studentType: Maybe<boolean>;

  @Field(type => DateTime)
  public notConfirmedAfter: Maybe<DateTime>;

  @Field(type => String)
  public kpfID: Maybe<string>;

  @Field(type => String)
  public createdBy: Maybe<string>;

  @Field(type => Boolean)
  public showGuest: Maybe<boolean>;

  @Field(type => Boolean)
  public neverChangedPassword: Maybe<boolean>;

  @Field(type => DateTime)
  public changedPasswordBefore: Maybe<DateTime>;

  @Field(type => [String])
  public preferedSubject: Maybe<string[]>;

  @Field(type => Int)
  public jahrgang: Maybe<number>;

  @Field(type => [String])
  public hasTags: Maybe<string[]>;

  override getMongoFilter(): Dictionary<any> {
    return { $and: [...this.getFilterArray()] };
  }

  override getFilterArray(): Array<Dictionary<any>> {
    const filter = this.toObject();

    const filterSchuelertyp =
      filter.studentType === null
        ? {}
        : filter.studentType
        ? { schuelertyp: StudentType.OS }
        : { schuelertyp: StudentType.US };

    const filterGuest =
      filter.showGuest === null ? {} : filter.showGuest ? { guestAccount: true } : { guestAccount: false };

    const filterJahrgangstufeConfirmed = filter.notConfirmedAfter
      ? {
          $or: [
            { 'schoolInformation.lastChangeOnJahrgangsstufe': null },
            {
              'schoolInformation.lastChangeOnJahrgangsstufe': {
                $lte: DateTime.fromJSDate(filter.notConfirmedAfter).startOf('day').toJSDate(),
              },
            },
          ],
        }
      : {};

    // const filterAktiv =
    //   filter.active === true
    //     ? {
    //         $or: [
    //           { 'kuendigung.austrittsdatum': { $exists: false } },
    //           { 'kuendigung.austrittsdatum': null },
    //           { 'kuendigung.austrittsdatum': { $gt: filter.activeOnDay ?? DateTime.now().toJSDate() } },
    //         ],
    //       }
    //     : filter.active === false
    //     ? { 'kuendigung.austrittsdatum': { $lt: filter.activeOnDay ?? DateTime.now().toJSDate() } }
    //     : {};

    const filterSimple = buildSimpleFilterMapped(filter, {
      butBerechtigung: 'buts.BuTBerechtigung',
      paymentMethod: 'bankInformation.zahlungsart',
      createdBy: 'createdBy',
    });

    const filterPasswordChanged = (() => {
      if (filter.neverChangedPassword) {
        return {
          $or: [
            {
              lastPasswordChange: {
                $exists: false,
              },
            },
            {
              lastPasswordChange: {
                $in: [null, undefined],
              },
            },
          ],
        };
      }

      if (filter.changedPasswordBefore) {
        return {
          lastPasswordChange: {
            $lte: filter.changedPasswordBefore,
          },
        };
      }

      return {};
    })();

    const filterPreferedSubject = filter.preferedSubject
      ? {
          $or: [
            {
              preferedFirstSubject: {
                $in: filter.preferedSubject,
              },
            },
            {
              preferedSecondSubject: {
                $in: filter.preferedSubject,
              },
            },
            {
              preferedThirdSubject: {
                $in: filter.preferedSubject,
              },
            },
          ],
        }
      : {};

    const filterJahrgangsstufe = filter.jahrgang
      ? {
          'schoolInformation.jahrgangsstufe': filter.jahrgang,
        }
      : {};

    const filterHasTags = filter.hasTags
      ? {
          tags: {
            $all: filter.hasTags,
          },
        }
      : {};

    return [
      {
        _class: Student._class,
      },
      ...super.getFilterArray(),
      filterPasswordChanged,
      // filterAktiv,
      filterSchuelertyp,
      filterGuest,
      filterJahrgangstufeConfirmed,
      filterJahrgangsstufe,
      filterPreferedSubject,
      filterSimple,
      filterHasTags,
    ];
  }
}
